import React, { useState } from 'react';
import { 
  Mail, 
  Calendar, 
  User, 
  MoreVertical, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Briefcase
} from 'lucide-react';

/**
 * ApplicationCard - Card component for displaying an applicant
 * @param {Object} application - The application data
 * @param {Object} stage - The current stage configuration
 * @param {Array} allStages - All pipeline stages
 * @param {Function} onMoveStage - Callback to move to different stage
 * @param {Function} onScheduleInterview - Callback to schedule interview
 * @param {Function} onSendEmail - Callback to send email
 * @param {Function} onViewDetails - Callback to view full details
 */
const ApplicationCard = ({ 
  application, 
  stage, 
  allStages,
  onMoveStage, 
  onScheduleInterview, 
  onSendEmail, 
  onViewDetails 
}) => {
  const [showActions, setShowActions] = useState(false);

  // Calculate days since application
  const getDaysSince = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysSince = getDaysSince(application.created_at);

  // Get initials for avatar
  const getInitials = () => {
    const first = application.first_name?.[0] || '';
    const last = application.last_name?.[0] || '';
    return (first + last).toUpperCase();
  };

  // Get stage color for border accent
  const getStageColor = () => {
    const colorMap = {
      'gray': 'border-l-gray-400',
      'blue': 'border-l-blue-400',
      'yellow': 'border-l-yellow-400',
      'purple': 'border-l-purple-400',
      'orange': 'border-l-orange-400',
      'green': 'border-l-green-400',
      'red': 'border-l-red-400'
    };
    return colorMap[stage?.color] || 'border-l-gray-400';
  };

  // Get current stage index for navigation
  const currentStageIndex = allStages.findIndex(s => s.name === application.current_stage);
  const canMoveLeft = currentStageIndex > 0;
  const canMoveRight = currentStageIndex < allStages.length - 1 && 
                       application.current_stage !== 'hired' && 
                       application.current_stage !== 'rejected';

  const handleMoveLeft = (e) => {
    e.stopPropagation();
    if (canMoveLeft) {
      onMoveStage(application.id, allStages[currentStageIndex - 1].name);
    }
  };

  const handleMoveRight = (e) => {
    e.stopPropagation();
    if (canMoveRight) {
      onMoveStage(application.id, allStages[currentStageIndex + 1].name);
    }
  };

  const assignedRecruiter = application.assigned_to_user;

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${getStageColor()} 
                  hover:shadow-md transition-all cursor-pointer group relative`}
      onClick={() => onViewDetails(application)}
    >
      {/* Card Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-purple-700 font-semibold text-sm">
                {getInitials()}
              </span>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                {application.first_name} {application.last_name}
              </h3>
              <p className="text-xs text-gray-500 truncate flex items-center">
                <Mail size={10} className="mr-1 flex-shrink-0" />
                {application.email}
              </p>
            </div>
          </div>
          
          {/* Quick Actions Dropdown */}
          <div className="relative">
            <button 
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 rounded"
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
            >
              <MoreVertical size={18} />
            </button>
            
            {showActions && (
              <>
                <div 
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActions(false);
                  }}
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(application);
                      setShowActions(false);
                    }}
                  >
                    <User size={14} className="mr-2" />
                    View Details
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onScheduleInterview(application);
                      setShowActions(false);
                    }}
                  >
                    <Calendar size={14} className="mr-2" />
                    Schedule Interview
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSendEmail(application);
                      setShowActions(false);
                    }}
                  >
                    <Mail size={14} className="mr-2" />
                    Send Email
                  </button>
                  <hr className="my-1" />
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveStage(application.id, 'rejected', 'Rejected from menu');
                      setShowActions(false);
                    }}
                  >
                    Move to Rejected
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Position */}
        <div className="mb-3">
          <p className="text-xs text-gray-600 flex items-center">
            <Briefcase size={12} className="mr-1 text-purple-500" />
            <span className="font-medium">{application.desired_position || 'No position specified'}</span>
          </p>
        </div>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center">
            <Clock size={12} className="mr-1" />
            <span className={daysSince > 7 ? 'text-orange-600 font-medium' : ''}>
              {daysSince === 0 ? 'Today' : daysSince === 1 ? '1 day ago' : `${daysSince} days ago`}
            </span>
          </div>
          
          {assignedRecruiter ? (
            <div className="flex items-center" title={`Assigned to: ${assignedRecruiter.first_name} ${assignedRecruiter.last_name}`}>
              <User size={12} className="mr-1 text-purple-500" />
              <span className="truncate max-w-[80px]">
                {assignedRecruiter.first_name} {assignedRecruiter.last_name?.[0]}.
              </span>
            </div>
          ) : (
            <span className="text-gray-400 italic">Unassigned</span>
          )}
        </div>
      </div>

      {/* Stage Navigation Footer */}
      <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between bg-gray-50 rounded-b-lg">
        <button
          className={`p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded ${canMoveLeft 
            ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900' 
            : 'text-gray-300 cursor-not-allowed'}`}
          onClick={handleMoveLeft}
          disabled={!canMoveLeft}
          title={canMoveLeft ? `Move to ${allStages[currentStageIndex - 1]?.label}` : ''}
        >
          <ChevronLeft size={18} />
        </button>
        
        <span className="text-xs text-gray-500 font-medium">
          {stage?.label || application.current_stage}
        </span>
        
        <button
          className={`p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded ${canMoveRight 
            ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900' 
            : 'text-gray-300 cursor-not-allowed'}`}
          onClick={handleMoveRight}
          disabled={!canMoveRight}
          title={canMoveRight ? `Move to ${allStages[currentStageIndex + 1]?.label}` : ''}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default ApplicationCard;
